import { useState, useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { animate } from 'motion/react'

import { Flex, Skeleton, Text } from '@buffer-mono/popcorn'

import {
  formatCompactCount,
  formatCompactCountPair,
  formatUnrepliedCount,
  getProgressColor,
} from './commentMetricsUtils'
import type { CommentCounts } from './commentMetricsUtils'
import {
  CompactCommentMetrics,
  GridCommentMetrics,
  ListCommentMetrics,
} from './CommentMetricsVariants'
import type { MetricsData } from './CommentMetricsVariants'
import { useCommentCountsForPost } from '../../context/CommentsByPostCountsContext'

import styles from './CommentMetrics.module.css'

// Re-export utils so existing consumers don't need to update their imports
export {
  type CommentCounts,
  formatCompactCount,
  formatCompactCountPair,
  formatUnrepliedCount,
  getProgressColor,
}

/** Animates from previous value to `target` using motion's `animate`. Starts from 0 on first mount. */
function useAnimateProgressFill(target: number): number {
  const [value, setValue] = useState(0)
  const prevTarget = useRef(0)

  useEffect(() => {
    const from = prevTarget.current
    prevTarget.current = target

    if (target === 0) {
      setValue(0)
      return
    }
    const delta = Math.abs(target - from)
    const duration = Math.max((delta / 100) * 0.8, 0.3)
    const controls = animate(from, target, {
      duration,
      ease: [0.25, 0.1, 0.25, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    })
    return (): void => controls.stop()
  }, [target])

  return value
}

type CommentMetricsBase = {
  postId: string
  /** Optional override: counts from API. When omitted, uses context (CommentsByPostCountsProvider). */
  counts?: CommentCounts | null
  /** Optional override: loading state. When omitted, uses context. */
  loading?: boolean
  /** Optional className for the container. */
  className?: string
}

export type CommentMetricsProps = CommentMetricsBase &
  (
    | {
        /** Rendering variant: grid (thumbnail cards) or list (text cards). */
        variant: 'grid' | 'list'
        /** Whether the card is hovered or selected. Controls hover-specific display. */
        isActive?: boolean
      }
    | {
        /** Rendering variant: compact (sticky nav header). No hover interactions. */
        variant: 'compact'
        isActive?: never
      }
  )

export function CommentMetrics({
  postId,
  counts: countsProp,
  loading: loadingProp,
  isActive = false,
  variant,
  className,
}: CommentMetricsProps): JSX.Element | null {
  const {
    counts: contextCounts,
    loading: contextLoading,
    error: contextError,
  } = useCommentCountsForPost(postId)
  const counts = countsProp !== undefined ? countsProp : contextCounts
  const loading = loadingProp !== undefined ? loadingProp : contextLoading
  const error = countsProp === undefined ? contextError : null

  const countsForDisplay: CommentCounts = counts ?? {
    unreplied: 0,
    replied: 0,
    dismissed: 0,
  }
  const total =
    countsForDisplay.unreplied +
    countsForDisplay.replied +
    countsForDisplay.dismissed
  const interacted = countsForDisplay.replied + countsForDisplay.dismissed
  const percentage = total > 0 ? Math.round((interacted / total) * 100) : 0
  const animatedPercentage = useAnimateProgressFill(percentage)

  if (loading) {
    return (
      <Flex
        align="center"
        gap="xs"
        fullWidth
        aria-busy
        aria-label="Loading comment metrics"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This Flex container is the live loading status while retaining its skeleton layout.
        role="status"
      >
        <Skeleton
          height={8}
          width={80}
          rounded
          className={styles.progressBar}
        />
        <Skeleton height={20} width={28} rounded />
      </Flex>
    )
  }

  if (error) {
    return (
      <Text size="sm" color="critical" as="span">
        Unable to load comment metrics
      </Text>
    )
  }

  const [interactedFormatted, totalFormatted] = formatCompactCountPair(
    interacted,
    total,
  )
  const hasComments = total > 0
  const isAllAddressed = hasComments && countsForDisplay.unreplied === 0
  const color = hasComments ? getProgressColor(percentage) : undefined

  const data: MetricsData = {
    isAllAddressed,
    hasComments,
    animatedPercentage,
    percentage,
    color,
    countsForDisplay,
    total,
    interactedFormatted,
    totalFormatted,
  }

  if (variant === 'grid') {
    return (
      <GridCommentMetrics
        data={data}
        isActive={isActive}
        className={className}
      />
    )
  }
  if (variant === 'list') {
    return (
      <ListCommentMetrics
        data={data}
        isActive={isActive}
        className={className}
      />
    )
  }
  return <CompactCommentMetrics data={data} className={className} />
}
